package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import com.qrkontaq.ui.tenant.InlineIcon
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px

/** Glass card matching the qp- command-center theme. */
@Composable
internal fun QpCard(
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) {
    Column(modifier.fillMaxWidth().attrsModifier { classes("qp-card") }) { content() }
}

/** Uppercase section eyebrow used at the top of a card. */
@Composable
internal fun QpTitle(text: String) {
    SpanText(text, Modifier.attrsModifier { classes("qp-card-title") })
}

/** KPI tile: icon chip + big Unbounded value + label. [live] adds a pulsing dot. */
@Composable
internal fun QpKpi(
    iconSvg: String,
    label: String,
    value: String,
    accent: String,
    live: Boolean = false,
) {
    Row(
        Modifier.attrsModifier { classes("qp-kpi") },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            Modifier
                .attrsModifier { classes("qp-kpi-icon") }
                .styleModifier { property("background", "${accent}22") },
            contentAlignment = Alignment.Center,
        ) {
            InlineIcon(iconSvg, 18, accent)
        }
        Column(Modifier.styleModifier { property("min-width", "0") }) {
            Row(Modifier.gap(7.px), verticalAlignment = Alignment.CenterVertically) {
                SpanText(value, Modifier.attrsModifier { classes("qp-kpi-value") })
                if (live) {
                    Box(
                        Modifier.size(8.px).borderRadius(999.px)
                            .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x34, 0xD3, 0x99))
                            .attrsModifier { classes("qp-pulse") },
                    )
                }
            }
            SpanText(label, Modifier.attrsModifier { classes("qp-kpi-label") })
        }
    }
}

/** Pill filter chip (period / site). */
@Composable
internal fun QpChip(
    label: String,
    active: Boolean,
    onClick: () -> Unit,
) {
    Box(
        Modifier
            .cursor(Cursor.Pointer)
            .attrsModifier { classes(*listOfNotNull("qp-chip", "on".takeIf { active }).toTypedArray()); attr("role", "button") }
            .onClick { onClick() },
    ) {
        SpanText(label)
    }
}

internal data class QpRank(
    val label: String,
    val count: Long,
    val extra: String? = null,
    val flag: String? = null,
)

/** Ranked rows with a proportional emerald bar behind each label. */
@Composable
internal fun QpRankedList(
    entries: List<QpRank>,
    trailingHint: String? = null,
    emptyLabel: String? = null,
) {
    if (entries.isEmpty()) {
        SpanText(
            emptyLabel ?: PlatformT.noData,
            Modifier.fontSize(13.px).color(com.varabyte.kobweb.compose.ui.graphics.Color.rgba(242, 247, 244, 0.55f)),
        )
        return
    }
    if (trailingHint != null) {
        Row(Modifier.fillMaxWidth().margin(bottom = 6.px), horizontalArrangement = Arrangement.End) {
            SpanText(
                trailingHint,
                Modifier.fontSize(10.px).color(com.varabyte.kobweb.compose.ui.graphics.Color.rgba(242, 247, 244, 0.45f)),
            )
        }
    }
    val max = entries.maxOf { it.count }.coerceAtLeast(1)
    Column(Modifier.fillMaxWidth().gap(6.px)) {
        entries.forEach { entry ->
            Box(Modifier.fillMaxWidth().attrsModifier { classes("qp-rank") }) {
                Box(
                    Modifier
                        .attrsModifier { classes("qp-rank-fill") }
                        .styleModifier { property("width", "${(entry.count * 100 / max).coerceAtLeast(2)}%") },
                )
                Row(
                    Modifier.fillMaxWidth().attrsModifier { classes("qp-rank-row") }.gap(10.px),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween,
                ) {
                    Row(Modifier.gap(8.px), verticalAlignment = Alignment.CenterVertically) {
                        if (entry.flag != null) SpanText(entry.flag, Modifier.fontSize(14.px))
                        SpanText(
                            entry.label,
                            Modifier.fontSize(12.px).color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xF2, 0xF7, 0xF4))
                                .attrsModifier { classes("qk-clamp1") }.styleModifier { property("min-width", "0") },
                        )
                    }
                    Row(Modifier.gap(8.px), verticalAlignment = Alignment.CenterVertically) {
                        SpanText(
                            "${entry.count}",
                            Modifier.fontSize(12.px).fontWeight(FontWeight.Bold)
                                .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xF2, 0xF7, 0xF4))
                                .attrsModifier { classes("qk-nowrap") },
                        )
                        if (entry.extra != null) {
                            SpanText(
                                entry.extra,
                                Modifier.fontSize(11.px)
                                    .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgba(242, 247, 244, 0.5f))
                                    .attrsModifier { classes("qk-nowrap") },
                            )
                        }
                    }
                }
            }
        }
    }
}

/** Two equal columns that wrap on narrow screens. */
@Composable
internal fun QpTwoColumn(
    first: @Composable () -> Unit,
    second: @Composable () -> Unit,
) {
    Row(Modifier.fillMaxWidth().flexWrap(org.jetbrains.compose.web.css.FlexWrap.Wrap).gap(14.px)) {
        Box(Modifier.styleModifier { property("flex", "1"); property("min-width", "300px") }) {
            Column(Modifier.fillMaxWidth()) { first() }
        }
        Box(Modifier.styleModifier { property("flex", "1"); property("min-width", "300px") }) {
            Column(Modifier.fillMaxWidth()) { second() }
        }
    }
}

private fun codePoints(first: Int, second: Int): String = js("String.fromCodePoint(first, second)") as String

/** Country ISO-2 -> flag emoji (regional indicator symbols). */
internal fun countryFlag(iso: String): String {
    if (iso.length != 2) return "🏳️"
    val a = iso[0].uppercaseChar()
    val b = iso[1].uppercaseChar()
    if (a !in 'A'..'Z' || b !in 'A'..'Z') return "🏳️"
    val base = 0x1F1E6
    return codePoints(base + (a.code - 'A'.code), base + (b.code - 'A'.code))
}

/** Human country name from ISO-2 via Intl.DisplayNames, falling back to the code. */
internal fun countryName(iso: String): String =
    try {
        val dn = js("new Intl.DisplayNames([\"en\"], { type: \"region\" })")
        (dn.of(iso.uppercase()) as? String) ?: iso.uppercase()
    } catch (_: Throwable) {
        iso.uppercase()
    }

// ---- shared formatting ----

internal fun money(
    minor: Long,
    currency: String,
): String {
    val symbol =
        when (currency.uppercase()) {
            "EUR" -> "€"
            "UAH" -> "₴"
            "USD" -> "$"
            else -> currency
        }
    val cents = (if (minor < 0) -minor else minor) % 100
    return "$symbol${minor / 100}.${cents.toString().padStart(2, '0')}"
}

internal fun isoDay(value: String?): String = value?.take(10)?.takeIf { it.isNotBlank() } ?: "—"

internal fun platformPlanLabel(plan: String?): String =
    when (plan) {
        "MONTH" -> PlatformT.planMonth
        "QUARTER" -> PlatformT.planQuarter
        "YEAR" -> PlatformT.planYear
        else -> "—"
    }

internal fun formatDuration(totalSeconds: Long): String {
    val minutes = totalSeconds / 60
    val seconds = totalSeconds % 60
    return "$minutes:${seconds.toString().padStart(2, '0')}"
}
